/**
 * Accepted file extensions for media upload validation
 * These constants define which file types are supported for upload
 */

export const ACCEPTED_VIDEO_FILE_EXTENSIONS = [
  '3dostr',
  '4xm',
  'avi',
  'avs',
  'avs2',
  'avs3',
  'bink',
  'bmv',
  'cdxl',
  'cine',
  'dav',
  'dfa',
  'dv',
  'flv',
  'gdv',
  'h261',
  'h263',
  'h264',
  'hevc',
  'hnm',
  'ivf',
  'ivr',
  'kux',
  'm4v',
  'mjpeg',
  'mkv',
  'mov',
  'mp4',
  '3gp',
  '3g2',
  'mj2',
  'mpeg',
  'mpg',
  'mts',
  'mxf',
  'mxg',
  'nsv',
  'nuv',
  'obu',
  'r3d',
  'rm',
  'roq',
  'smk',
  'swf',
  'ts',
  'vc1',
  'viv',
  'vob',
  'webm',
  'wtv',
  'xmv',
  'yuv',
] as const;

export const ACCEPTED_IMAGE_FILE_EXTENSIONS = [
  'apng',
  'avci',
  'avcs',
  'bmp',
  'dpx',
  'exr',
  'fits',
  'gif',
  'heic',
  'heics',
  'heif',
  'heifs',
  'hif',
  'ico',
  'jpeg',
  'jpg',
  'msp',
  'pam',
  'pbm',
  'pcx',
  'pgm',
  'photocd',
  'pictor',
  'png',
  'ppm',
  'psd',
  'qdraw',
  'sgi',
  'sunrast',
  'svg',
  'tiff',
  'tif',
  'webp',
  'xbm',
  'xpm',
  'xwd',
] as const;

export const ACCEPTED_AUDIO_FILE_EXTENSIONS = [
  'aac',
  'ac3',
  'aif',
  'aiff',
  'alac',
  'amr',
  'ape',
  'au',
  'dts',
  'eac3',
  'flac',
  'gsm',
  'm4a',
  'mka',
  'mp2',
  'mp3',
  'oga',
  'ogg',
  'opus',
  'ra',
  'shn',
  'tta',
  'voc',
  'wav',
  'wma',
  'wv',
] as const;

/**
 * Image extensions accepted for image search queries.
 * Limited to formats that browsers render natively (image/* MIME) and that
 * PIL (Pillow) can process on the backend — excludes SVG, APNG (single-frame
 * only in PIL), and HEIC/HEIF (requires pillow-heif plugin).
 */
export const ACCEPTED_IMAGE_SEARCH_EXTENSIONS = [
  'avif',
  'bmp',
  'gif',
  'ico',
  'jpg',
  'jpeg',
  'png',
  'tif',
  'tiff',
  'webp',
] as const;
// `image/*` is intentionally included so mobile browsers show the native
// camera / photo-library picker instead of a generic file browser.
// The explicit extensions are appended as a fallback for desktop browsers.
// Actual validation happens at runtime via `isAcceptedImageFile`.
export const ACCEPTED_IMAGE_SEARCH_EXTENSIONS_AS_ACCEPT_STRING = [
  'image/*',
  ...ACCEPTED_IMAGE_SEARCH_EXTENSIONS.map((ext) => `.${ext}`),
].join(',');

/**
 * Sets for O(1) extension lookup
 * Created from the const arrays for efficient membership testing
 */
export const ACCEPTED_VIDEO_FILE_EXTENSIONS_SET = new Set(
  ACCEPTED_VIDEO_FILE_EXTENSIONS as readonly string[],
);
export const ACCEPTED_IMAGE_FILE_EXTENSIONS_SET = new Set(
  ACCEPTED_IMAGE_FILE_EXTENSIONS as readonly string[],
);
export const ACCEPTED_AUDIO_FILE_EXTENSIONS_SET = new Set(
  ACCEPTED_AUDIO_FILE_EXTENSIONS as readonly string[],
);
export const ACCEPTED_IMAGE_SEARCH_EXTENSIONS_SET = new Set(
  ACCEPTED_IMAGE_SEARCH_EXTENSIONS as readonly string[],
);

/**
 * Check whether a file is accepted for image search.
 *
 * Used by:
 * - Drag-and-drop handler (`handleDrop` in SearchBar)
 * - Clipboard paste handler (`onPaste` in SearchBar)
 * - File picker handler (`handleFileInputChange` in SearchBar)
 *
 * Requires an image/* MIME type (excludes SVG) **and** an extension present
 * in ACCEPTED_IMAGE_SEARCH_EXTENSIONS so the runtime check stays in sync
 * with the file picker's `accept` attribute.
 */
/**
 * Check whether a filename has an audio extension.
 * Used to decide whether to show an audio icon instead of a placeholder gradient.
 */
export const isAudioFile = (filename: string): boolean => {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return ACCEPTED_AUDIO_FILE_EXTENSIONS_SET.has(ext);
};

/**
 * Check whether a pre-upload item is an image: by MIME type, or by extension, since browsers leave
 * some images untyped or generically typed (HEIC on several platforms). URL-import items carry
 * only a MIME type.
 */
export const isImageItem = (item: { name?: string; type?: string }): boolean => {
  if (item.type?.startsWith('image/')) return true;
  const ext = item.name?.split('.').pop()?.toLowerCase() ?? '';
  return ACCEPTED_IMAGE_FILE_EXTENSIONS_SET.has(ext);
};

/** Check whether a filename has a video extension. */
export const isVideoFile = (filename: string): boolean => {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return ACCEPTED_VIDEO_FILE_EXTENSIONS_SET.has(ext);
};

/**
 * Check whether a backend Media object is audio-only.
 * Uses metadata.video dimensions (the backend sets width/height/frameCount to 0 for audio).
 * Falls back to filename extension when metadata.video is not yet available.
 */
export const isAudioMedia = (media: {
  metadata?: { video?: { width: number; height: number }; filename?: string; name?: string };
}): boolean => {
  const video = media.metadata?.video;
  if (video) {
    return video.width === 0 && video.height === 0;
  }
  return isAudioFile(media.metadata?.filename || media.metadata?.name || '');
};

export const isAcceptedImageFile = (file: File): boolean => {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return false;
  }
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ACCEPTED_IMAGE_SEARCH_EXTENSIONS_SET.has(ext);
};
